import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from "@apollo/client/react";
import { useCallback } from "react";
import ReactGA from "react-ga4";
import { ME, REGISTER, UPDATE_CURRENT_USER_PROFILE } from "@/hooks";
import { useAuth, useUser } from "@/app/providers";
import { fillUserWithMe } from "@/utils";

type RegisterCredentials = {
  email?: string;
  name?: string;
  password?: string;
  password_confirmation?: string;
  host?: string;
};

type UpdateProfileVariables = Record<string, unknown>;

/**
 * Custom hook to register a new user.
 * @param onCompleted
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{submit: Function, isSubmitting: boolean, registrationSuccess: boolean}} - The function to submit the registration request, the loading state, and the success state.
 */
export function useRegister(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const { setToken } = useAuth();
  const { setUser } = useUser();
  const apolloClient = useApolloClient();

  const [getMe, { loading: getting }] = useLazyQuery(ME);
  const [comRegister, { loading: submitting }] = useMutation<any>(REGISTER);
  const [comUpdateAccount] = useMutation<any>(UPDATE_CURRENT_USER_PROFILE);

  const updateProfile = useCallback(
    async (variables: UpdateProfileVariables) => {
      try {
        await comUpdateAccount({
          variables,
          refetchQueries: [{ query: ME }],
        });
      } catch {
        // Silently fail
      }
    },
    [comUpdateAccount],
  );

  const submit = useCallback(
    (credentials: RegisterCredentials) => {
      try {
        comRegister({
          variables: credentials,
        })
          .then((data) => {
            const registerResult =
              data?.data?.register ?? data?.data?.comRegister ?? {};
            if (registerResult?.success) {
              if (registerResult?.token) {
                setToken(registerResult.token);

                let count = 0;
                const maxTries = 30;
                // Writing login token in cookies takes some time so why we added sleep.
                const intervalId = setInterval(() => {
                  getMe()
                    .then((data) => {
                      if (count++ > maxTries) {
                        clearInterval(intervalId);
                      }
                      let meData = {};
                      if (data?.data) {
                        meData = data?.data;
                      }
                      // @ts-ignore
                      // @ts-expect-error
                      if (meData?.me) {
                        clearInterval(intervalId);
                        fillUserWithMe(
                          (meData as any).me,
                          apolloClient,
                          setUser,
                          updateProfile,
                          true,
                        )
                          .then((finalMe) => {
                            setUser({ ...finalMe });
                          })
                          .catch(() => {
                            // Still set user even if fillUserWithMe fails
                            setUser({ ...(meData as any).me });
                          });
                      }
                      onCompleted(meData);
                    })
                    .catch((error) => {
                      clearInterval(intervalId);
                      onError(
                        error instanceof Error ? error.message : String(error),
                      );
                    });
                }, 1000);
              }
            } else {
              onError(registerResult?.message?.[0] ?? "Register failed");
            }
          })
          .catch((error) => {
            onError(error instanceof Error ? error.message : String(error));
          });

        ReactGA.event({
          category: "Authentication",
          action: "Register",
          label: credentials?.email.replace("@", "[at]"),
        });
      } catch (err) {
        onError(err instanceof Error ? err.message : String(err));
      }
    },
    [
      comRegister,
      getMe,
      setToken,
      setUser,
      onCompleted,
      onError,
      apolloClient,
      updateProfile,
    ],
  );

  return {
    submit,
    isSubmitting: submitting || getting,
  };
}
